export type Priority = 'Critical' | 'High' | 'Medium' | 'Low'
export type Status =
  | 'Open'
  | 'In Progress'
  | 'Waiting on User'
  | 'Resolved'
  | 'Closed'
  | 'Overdue'
export type Department =
  | 'Loan Officers'
  | 'Processors'
  | 'Closers'
  | 'Underwriting'
  | 'Compliance'
  | 'IT'
export type Channel = 'Email' | 'Portal' | 'Slack' | 'Phone'
export type Category =
  | 'System Access'
  | 'Salesforce Issue'
  | 'Email Problem'
  | 'VPN/Network'
  | 'Hardware'
  | 'Software Install'
  | 'Password Reset'
  | 'Printer Issue'

export interface Ticket {
  id: string
  subject: string
  submittedBy: string
  department: Department
  role: string
  priority: Priority
  status: Status
  assignedTo: string
  createdDate: string
  firstResponseTime: number // in minutes
  resolutionTime: number // in hours
  channel: Channel
  category: Category
}

export interface TeamMember {
  id: string
  name: string
  activeTickets: number
  avgResponseTime: number // in minutes
  avgResolutionTime: number // in hours
  status: 'Available' | 'Busy' | 'Away'
}

export interface KBArticle {
  id: string
  title: string
  views: number
  deflectedTickets: number
}

export const mockTickets: Ticket[] = [
  {
    id: 'INC-1042',
    subject: 'Cannot access Encompass',
    submittedBy: 'Sarah Jenkins',
    department: 'Loan Officers',
    role: 'Senior LO',
    priority: 'Critical',
    status: 'Overdue',
    assignedTo: 'Mike Ross',
    createdDate: '2023-10-24T08:30:00Z',
    firstResponseTime: 45,
    resolutionTime: 26,
    channel: 'Phone',
    category: 'System Access',
  },
  {
    id: 'INC-1043',
    subject: 'Salesforce dashboard not loading',
    submittedBy: 'David Chen',
    department: 'Processors',
    role: 'Processor',
    priority: 'High',
    status: 'In Progress',
    assignedTo: 'Alex Wong',
    createdDate: '2023-10-25T09:15:00Z',
    firstResponseTime: 12,
    resolutionTime: 0,
    channel: 'Portal',
    category: 'Salesforce Issue',
  },
  {
    id: 'INC-1044',
    subject: 'Need password reset for email',
    submittedBy: 'Amanda Smith',
    department: 'Closers',
    role: 'Closer',
    priority: 'Low',
    status: 'Resolved',
    assignedTo: 'Sarah Connor',
    createdDate: '2023-10-25T10:05:00Z',
    firstResponseTime: 5,
    resolutionTime: 0.5,
    channel: 'Slack',
    category: 'Password Reset',
  },
  {
    id: 'INC-1045',
    subject: 'VPN connection dropping',
    submittedBy: 'James Wilson',
    department: 'Underwriting',
    role: 'Underwriter',
    priority: 'High',
    status: 'Open',
    assignedTo: 'Unassigned',
    createdDate: '2023-10-25T11:20:00Z',
    firstResponseTime: 0,
    resolutionTime: 0,
    channel: 'Portal',
    category: 'VPN/Network',
  },
  {
    id: 'INC-1046',
    subject: 'Printer on 3rd floor jammed',
    submittedBy: 'Maria Garcia',
    department: 'Compliance',
    role: 'Auditor',
    priority: 'Medium',
    status: 'Waiting on User',
    assignedTo: 'Mike Ross',
    createdDate: '2023-10-25T13:45:00Z',
    firstResponseTime: 20,
    resolutionTime: 0,
    channel: 'Email',
    category: 'Printer Issue',
  },
  {
    id: 'INC-1047',
    subject: 'Need Adobe Acrobat Pro installed',
    submittedBy: 'Robert Taylor',
    department: 'Loan Officers',
    role: 'LO Assistant',
    priority: 'Low',
    status: 'Open',
    assignedTo: 'Alex Wong',
    createdDate: '2023-10-25T14:10:00Z',
    firstResponseTime: 0,
    resolutionTime: 0,
    channel: 'Portal',
    category: 'Software Install',
  },
  {
    id: 'INC-1048',
    subject: 'Missing borrower documents in SF',
    submittedBy: 'Jessica Lee',
    department: 'Processors',
    role: 'Senior Processor',
    priority: 'Critical',
    status: 'In Progress',
    assignedTo: 'Sarah Connor',
    createdDate: '2023-10-25T14:30:00Z',
    firstResponseTime: 8,
    resolutionTime: 0,
    channel: 'Slack',
    category: 'Salesforce Issue',
  },
  {
    id: 'INC-1049',
    subject: 'New laptop setup',
    submittedBy: 'Thomas Brown',
    department: 'IT',
    role: 'IT Manager',
    priority: 'Medium',
    status: 'Closed',
    assignedTo: 'Mike Ross',
    createdDate: '2023-10-20T09:00:00Z',
    firstResponseTime: 15,
    resolutionTime: 48,
    channel: 'Portal',
    category: 'Hardware',
  },
  {
    id: 'INC-1050',
    subject: 'Cannot send emails to external clients',
    submittedBy: 'Emily Davis',
    department: 'Closers',
    role: 'Closer',
    priority: 'High',
    status: 'Overdue',
    assignedTo: 'Alex Wong',
    createdDate: '2023-10-24T15:20:00Z',
    firstResponseTime: 65,
    resolutionTime: 20,
    channel: 'Email',
    category: 'Email Problem',
  },
  {
    id: 'INC-1051',
    subject: 'Encompass pricing engine error',
    submittedBy: 'Michael Miller',
    department: 'Loan Officers',
    role: 'Branch Manager',
    priority: 'Critical',
    status: 'Open',
    assignedTo: 'Unassigned',
    createdDate: '2023-10-25T15:45:00Z',
    firstResponseTime: 0,
    resolutionTime: 0,
    channel: 'Phone',
    category: 'System Access',
  },
  {
    id: 'INC-1052',
    subject: "Compliance training video won't play",
    submittedBy: 'Lisa Anderson',
    department: 'Compliance',
    role: 'Compliance Officer',
    priority: 'Low',
    status: 'Resolved',
    assignedTo: 'Sarah Connor',
    createdDate: '2023-10-25T08:10:00Z',
    firstResponseTime: 30,
    resolutionTime: 2,
    channel: 'Email',
    category: 'Software Install',
  },
  {
    id: 'INC-1053',
    subject: 'Need access to shared drive',
    submittedBy: 'William Thomas',
    department: 'Underwriting',
    role: 'Junior Underwriter',
    priority: 'Medium',
    status: 'Waiting on User',
    assignedTo: 'Mike Ross',
    createdDate: '2023-10-25T10:30:00Z',
    firstResponseTime: 25,
    resolutionTime: 0,
    channel: 'Portal',
    category: 'System Access',
  },
  {
    id: 'INC-1054',
    subject: 'Monitor flickering',
    submittedBy: 'Ashley Jackson',
    department: 'Processors',
    role: 'Processor',
    priority: 'Medium',
    status: 'In Progress',
    assignedTo: 'Alex Wong',
    createdDate: '2023-10-25T11:45:00Z',
    firstResponseTime: 40,
    resolutionTime: 0,
    channel: 'Slack',
    category: 'Hardware',
  },
  {
    id: 'INC-1055',
    subject: 'Salesforce report export failing',
    submittedBy: 'Brian White',
    department: 'Loan Officers',
    role: 'Senior LO',
    priority: 'High',
    status: 'Open',
    assignedTo: 'Sarah Connor',
    createdDate: '2023-10-25T16:00:00Z',
    firstResponseTime: 0,
    resolutionTime: 0,
    channel: 'Portal',
    category: 'Salesforce Issue',
  },
  {
    id: 'INC-1056',
    subject: 'VPN password expired',
    submittedBy: 'Megan Harris',
    department: 'Closers',
    role: 'Closer Manager',
    priority: 'Medium',
    status: 'Resolved',
    assignedTo: 'Mike Ross',
    createdDate: '2023-10-25T13:15:00Z',
    firstResponseTime: 10,
    resolutionTime: 1,
    channel: 'Phone',
    category: 'Password Reset',
  },
]

export const mockTeamMembers: TeamMember[] = [
  {
    id: 'TM-1',
    name: 'Mike Ross',
    activeTickets: 8,
    avgResponseTime: 18,
    avgResolutionTime: 4.2,
    status: 'Busy',
  },
  {
    id: 'TM-2',
    name: 'Alex Wong',
    activeTickets: 12,
    avgResponseTime: 24,
    avgResolutionTime: 5.5,
    status: 'Busy',
  },
  {
    id: 'TM-3',
    name: 'Sarah Connor',
    activeTickets: 5,
    avgResponseTime: 12,
    avgResolutionTime: 3.1,
    status: 'Available',
  },
  {
    id: 'TM-4',
    name: 'John Doe',
    activeTickets: 0,
    avgResponseTime: 15,
    avgResolutionTime: 4.0,
    status: 'Away',
  },
  {
    id: 'TM-5',
    name: 'Jane Smith',
    activeTickets: 7,
    avgResponseTime: 20,
    avgResolutionTime: 4.8,
    status: 'Available',
  },
  {
    id: 'TM-6',
    name: 'Unassigned',
    activeTickets: 15,
    avgResponseTime: 0,
    avgResolutionTime: 0,
    status: 'Away',
  },
]

export const mockDepartmentData = [
  { name: 'Loan Officers', tickets: 45 },
  { name: 'Processors', tickets: 38 },
  { name: 'Closers', tickets: 25 },
  { name: 'Underwriting', tickets: 20 },
  { name: 'Compliance', tickets: 12 },
  { name: 'IT', tickets: 5 },
]

export const mockCategoryData = [
  { name: 'System Access', count: 52 },
  { name: 'Salesforce Issue', count: 48 },
  { name: 'Password Reset', count: 35 },
  { name: 'VPN/Network', count: 28 },
  { name: 'Hardware', count: 20 },
  { name: 'Software Install', count: 18 },
  { name: 'Email Problem', count: 15 },
  { name: 'Printer Issue', count: 8 },
]

export const mockTrendData = [
  { date: 'Mon', tickets: 42 },
  { date: 'Tue', tickets: 58 },
  { date: 'Wed', tickets: 45 },
  { date: 'Thu', tickets: 60 },
  { date: 'Fri', tickets: 55 },
  { date: 'Sat', tickets: 15 },
  { date: 'Sun', tickets: 12 },
]

export const mockKBArticles: KBArticle[] = [
  {
    id: 'KB-101',
    title: 'How to reset your Encompass password',
    views: 1245,
    deflectedTickets: 85,
  },
  {
    id: 'KB-102',
    title: 'Troubleshooting VPN connection drops',
    views: 980,
    deflectedTickets: 62,
  },
  {
    id: 'KB-103',
    title: 'Salesforce: Exporting reports to Excel',
    views: 850,
    deflectedTickets: 45,
  },
  {
    id: 'KB-104',
    title: 'Setting up email on mobile device',
    views: 720,
    deflectedTickets: 38,
  },
  {
    id: 'KB-105',
    title: 'Clearing browser cache for portal access',
    views: 650,
    deflectedTickets: 30,
  },
]

export const mockSuggestedArticles = [
  { issue: 'Adobe Acrobat license activation', count: 24, trend: '+15%' },
  { issue: 'Dual monitor display settings', count: 18, trend: '+5%' },
  { issue: 'Encompass pricing engine timeout', count: 15, trend: '+25%' },
]
